import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getToken, getUser } from '../utils/auth';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DOC_TYPE_LABELS = {
  SLC: 'School Leaving Certificate',
  TC: 'Transfer Certificate',
  BONAFIDE: 'Bonafide Certificate',
  RESULT: 'Results',
  REPORT_CARD: 'Report Cards',
};

const DOC_TYPE_ROUTES = {
  SLC: 'slc',
  TC: 'tc',
  BONAFIDE: 'bonafide',
  RESULT: 'results',
  REPORT_CARD: 'report-cards',
};

const DOC_TYPE_COLORS = {
  SLC: 'purple',
  TC: 'blue',
  BONAFIDE: 'green',
  RESULT: 'amber',
  REPORT_CARD: 'purple',
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { schoolId } = useParams();
  const [user, setUser] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = getToken(schoolId);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/documents/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to load');
      }
      setOverview(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const stored = getUser(schoolId);
    if (!stored || !token) {
      navigate('/login');
      return;
    }
    setUser(stored);
    loadDashboard();
  }, [navigate, token, loadDashboard]);

  if (loading) {
    return (
      <div className="dash-loading">
        <div className="dash-spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="doc-page">
      {/* Top bar */}
      <header className="dash-topbar">
        <div>
          <h1 className="page-title">Document Portal</h1>
          <p className="page-subtitle">Welcome back, {user?.userName}</p>
        </div>
        <div className="topbar-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}</span>
        </div>
      </header>

      {/* Error */}
      {error && (
        <div className="dash-error">
          <span>{error}</span>
          <button onClick={loadDashboard}>Retry</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon stat-icon-purple">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{overview?.totalDocTypes ?? 0}</span>
            <span className="stat-label">Document Types</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-green">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{overview?.assignedTemplates ?? 0}</span>
            <span className="stat-label">Templates Assigned</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-amber">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{schoolId}</span>
            <span className="stat-label">School ID</span>
          </div>
        </div>
      </div>

      {/* Document Types Grid */}
      <h3 className="section-title" style={{ marginBottom: 16 }}>Document Types</h3>
      <div className="doc-types-grid">
        {overview?.docTypes?.map((dt) => (
          <Link
            key={dt.type}
            to={`/${schoolId}/${DOC_TYPE_ROUTES[dt.type]}`}
            className="doc-type-card"
          >
            <div className={`doc-type-icon doc-type-icon-${DOC_TYPE_COLORS[dt.type]}`}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <div className="doc-type-info">
              <span className="doc-type-name">{DOC_TYPE_LABELS[dt.type]}</span>
              <span className="doc-type-status">
                {dt.assigned ? (
                  <><span className="status-dot status-dot-active" /> Template assigned</>
                ) : (
                  <><span className="status-dot status-dot-pending" /> No template</>
                )}
              </span>
            </div>
            <svg className="doc-type-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
